import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import {
  VOID_ENFORCEMENT_INTENT,
  VoidEnforcementIntentData,
  VoidEnforcementIntentVars,
  RECORD_ENFORCEMENT_INTENT_PAYMENT,
  RecordEnforcementIntentPaymentData,
  RecordEnforcementIntentPaymentVars,
} from "common/graphql";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import React from "react";
import { VoidNoticeDialog } from "@curbsuite/components/VoidNoticeDialog";
import { RecordNoticePaymentDialog } from "@curbsuite/components/RecordNoticePaymentDialog";

export interface NoticePostIssueActionsProps {
  id: string;
  showVoidButton: boolean;
  showRecordPaymentButton: boolean;
  refetch: () => void;
}

export const NoticePostIssueActions: React.FC<NoticePostIssueActionsProps> = ({
  id,
  showVoidButton,
  showRecordPaymentButton,
  refetch,
}) => {
  const [voidDialogOpen, setVoidDialogOpen] = React.useState(false);
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = React.useState(false);
  const { publishSnackbarMessage } = useSnackPackContext();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [voidEnforcementIntent, { loading: voidSubmitting }] = useMutation<
    VoidEnforcementIntentData,
    VoidEnforcementIntentVars
  >(VOID_ENFORCEMENT_INTENT);

  const [recordPayment, { loading: recordPaymentSubmitting }] = useMutation<
    RecordEnforcementIntentPaymentData,
    RecordEnforcementIntentPaymentVars
  >(RECORD_ENFORCEMENT_INTENT_PAYMENT);

  const handleVoidSubmit = async ({ reason, voidedAt }: { reason: string; voidedAt: Date }) => {
    try {
      await voidEnforcementIntent({ variables: { id, reason, voidedAt } });
      publishSnackbarMessage({ severity: "success", message: "Notice voided" });
      setVoidDialogOpen(false);
      refetch();
    } catch (err) {
      console.error("Failed to void notice", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to void notice" });
    }
  };

  const handleRecordPaymentSubmit = async ({ paidAmountCents, paidAt }: { paidAmountCents: number; paidAt: Date }) => {
    try {
      await recordPayment({ variables: { id, paidAmount: paidAmountCents, paidAt } });
      publishSnackbarMessage({ severity: "success", message: "Payment recorded" });
      setRecordPaymentDialogOpen(false);
      refetch();
    } catch (err) {
      console.error("Failed to record payment", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to record payment" });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: { xs: "space-between", sm: "flex-end" },
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
      }}
    >
      {showVoidButton && (
        <Button variant="contained" color="error" onClick={() => setVoidDialogOpen(true)} fullWidth={isMobile}>
          Void
        </Button>
      )}
      {showRecordPaymentButton && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => setRecordPaymentDialogOpen(true)}
          fullWidth={isMobile}
        >
          Record Payment
        </Button>
      )}
      <VoidNoticeDialog
        open={voidDialogOpen}
        onClose={() => setVoidDialogOpen(false)}
        onSubmit={handleVoidSubmit}
        entityLabel="Notice"
        isSubmitting={voidSubmitting}
      />
      <RecordNoticePaymentDialog
        open={recordPaymentDialogOpen}
        onClose={() => setRecordPaymentDialogOpen(false)}
        onSubmit={handleRecordPaymentSubmit}
        entityLabel="Notice"
        isSubmitting={recordPaymentSubmitting}
      />
    </Box>
  );
};

export default NoticePostIssueActions;
